/**
 * Les demandes de création de site du mode `?demo` : trois demandes fictives
 * (galeries inventées, jamais réelles), en mémoire. Les transitions se
 * rejouent comme l'API : `envoyee → en_cours | traitee | abandonnee`,
 * `en_cours → traitee | abandonnee`, sinon `409 transition_interdite`.
 */
import { ApiError } from './api.js';
import { demoApi } from './demoData.js';
import type { DemandeSiteConsole, EtatDemandeSite, FiltreDemandesSite } from '../types.js';

const JOUR = 86_400_000;
const depuis = (jours: number): string => new Date(Date.now() - jours * JOUR).toISOString();

const SUIVANTS: Record<EtatDemandeSite, readonly EtatDemandeSite[]> = {
  envoyee: ['en_cours', 'traitee', 'abandonnee'],
  en_cours: ['traitee', 'abandonnee'],
  traitee: [],
  abandonnee: [],
};

function galerie(slug: string, nom: string): DemandeSiteConsole['galerie'] {
  const connue = demoApi.listerGaleries().find((g) => g.slug === slug);
  return { id: connue?.id ?? `demo-${slug}`, slug, nom };
}

let demandes: DemandeSiteConsole[] | null = null;

function semer(): DemandeSiteConsole[] {
  return [
    {
      id: 'demo-demande-site-1',
      etat: 'envoyee',
      siteExistant: true,
      urlActuelle: 'https://galerie-du-marais.example',
      objectifs:
        'Présenter les artistes de la galerie et annoncer les expositions, avec une version anglaise pour nos collectionneurs étrangers.',
      langues: ['fr', 'en'],
      visiteVirtuelle: true,
      delai: 'trois_mois',
      budget: 'Autour de 8 000 €',
      message: 'Nous aimerions une première discussion avant la rentrée.',
      contact: {
        nom: 'Hélène Rivière',
        email: 'helene@galerie-du-marais.example',
        telephone: '01 02 03 04 05',
        moyen: 'telephone',
      },
      envoyeeLe: depuis(1),
      traiteeLe: null,
      galerie: galerie('galerie-du-marais', 'Galerie du Marais'),
      demandeurEmail: 'helene@galerie-du-marais.example',
    },
    {
      id: 'demo-demande-site-2',
      etat: 'en_cours',
      siteExistant: false,
      urlActuelle: null,
      objectifs: 'Un site sobre, qui montre nos œuvres disponibles.',
      langues: ['fr'],
      visiteVirtuelle: false,
      delai: 'des_que_possible',
      budget: null,
      message: '',
      contact: {
        nom: 'Claire Vallier',
        email: 'direction@sainte-avoye.example',
        telephone: null,
        moyen: 'email',
      },
      envoyeeLe: depuis(6),
      traiteeLe: null,
      galerie: galerie('sainte-avoye', 'Galerie Sainte-Avoye'),
      demandeurEmail: 'direction@sainte-avoye.example',
    },
    {
      id: 'demo-demande-site-3',
      etat: 'traitee',
      siteExistant: true,
      urlActuelle: 'https://atelier-nord.example',
      objectifs: 'Refaire le site autour des expositions.',
      langues: ['fr', 'en'],
      visiteVirtuelle: false,
      delai: 'sans_contrainte',
      budget: null,
      message: 'Merci de nous rappeler en visio.',
      contact: {
        nom: 'Paul Ferrand',
        email: 'paul@atelier-nord.example',
        telephone: null,
        moyen: 'visio',
      },
      envoyeeLe: depuis(40),
      traiteeLe: depuis(30),
      galerie: galerie('atelier-nord', 'Atelier Nord'),
      demandeurEmail: 'paul@atelier-nord.example',
    },
  ];
}

function etat(): DemandeSiteConsole[] {
  demandes ??= semer();
  return demandes;
}

export const demoDemandesSite = {
  lister(filtre: FiltreDemandesSite): DemandeSiteConsole[] {
    return etat()
      .filter((d) =>
        filtre === 'toutes'
          ? true
          : filtre === 'ouvertes'
            ? d.etat === 'envoyee' || d.etat === 'en_cours'
            : d.etat === filtre,
      )
      .sort((a, b) => b.envoyeeLe.localeCompare(a.envoyeeLe))
      .map((d) => structuredClone(d));
  },
  changerEtat(id: string, vers: EtatDemandeSite): { demande: DemandeSiteConsole } {
    const demande = etat().find((d) => d.id === id);
    if (!demande) throw new ApiError(404, 'introuvable', 'Demande introuvable.');
    if (!SUIVANTS[demande.etat].includes(vers)) {
      throw new ApiError(409, 'conflit.version', 'Cette demande ne peut pas passer à cet état.', {
        raison: 'transition_interdite',
      });
    }
    demande.etat = vers;
    if (vers === 'traitee' || vers === 'abandonnee') demande.traiteeLe = new Date().toISOString();
    return { demande: structuredClone(demande) };
  },
};
